import { useEffect, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { COVER_UNANSWERED_TEXT, checkCoverFile, isUnansweredCoverWrite } from '@/lib/template-cover';
import { TEMPLATE_COVER } from '@/lib/template-labels';
import { toastIfSilent } from '@/lib/template-toast';
import { formTemplatesService } from '@/services/formTemplates';
import toastService from '@/services/toast/service';
import { GatedButton } from './GatedButton';

// The template's cover: the picture on its card in every gallery. It is saved as soon as it uploads, outside the
// catalogue's Save and outside versions. Design: docs/specs/2026-10-13-template-covers-and-gallery-design.md

export const COVER_HELP = 'WebP, 1200 x 900, up to 1 MB. Saved as soon as it uploads, not part of a version.';
export const NO_COVER = 'No cover. Cards show a drawing of the first question.';
export const COVER_MISSING = 'The cover file is missing. Upload it again.';

const BOX = 'relative w-full overflow-hidden rounded-lg border bg-muted';
const IMAGE = 'absolute inset-0 h-full w-full object-cover';

interface CoverPreviewProps {
  /** null when the template has no cover. */
  coverUrl: string | null;
  /** The file at coverUrl couldn't be loaded. */
  missing: boolean;
  onError?: () => void;
}

/** The cover at 4:3, as the card in every gallery shows it. */
export function CoverPreview({ coverUrl, missing, onError }: CoverPreviewProps) {
  if (!coverUrl || missing) {
    return (
      <div
        className={`flex aspect-[4/3] w-full items-center justify-center rounded-lg border border-dashed bg-muted/50 p-6 text-center text-sm ${
          missing ? 'text-destructive' : 'text-muted-foreground'
        }`}
      >
        {missing ? COVER_MISSING : NO_COVER}
      </div>
    );
  }
  return (
    <div className={`${BOX} aspect-[4/3]`}>
      <img
        src={coverUrl}
        alt=""
        width={TEMPLATE_COVER.width}
        height={TEMPLATE_COVER.height}
        draggable={false}
        onError={onError}
        className={IMAGE}
      />
    </div>
  );
}

interface CoverUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  publicId: string;
  replacing: boolean;
  onBusyChange: (busy: boolean) => void;
  onChanged: () => Promise<unknown>;
}

function CoverUploadDialog({ open, onOpenChange, publicId, replacing, onBusyChange, onChanged }: CoverUploadDialogProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  // Only the latest pick's check may set the state: an earlier, slower one is dropped.
  const checkId = useRef(0);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      checkId.current++;
      setFile(null);
      setError(null);
      setChecking(false);
    }
  }, [open]);

  // The chosen file's preview lives as long as it stays chosen.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    // Reset so choosing the same file again fires another change event.
    e.target.value = '';
    if (!chosen) return;
    const id = ++checkId.current;
    setChecking(true);
    const problem = await checkCoverFile(chosen);
    if (id !== checkId.current) return;
    setChecking(false);
    setError(problem);
    setFile(problem ? null : chosen);
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    onBusyChange(true);
    try {
      await formTemplatesService.uploadTemplateCover(publicId, file);
      toastService.success('Cover saved');
      onOpenChange(false);
      await onChanged();
    } catch (err) {
      if (isUnansweredCoverWrite(err)) {
        // The cover may have been saved: show what the server has, don't offer a second upload.
        toastService.error(COVER_UNANSWERED_TEXT);
        onOpenChange(false);
        await onChanged();
      } else {
        // A server message (an invalid file, or the cover changed meanwhile) was already toasted by the api client.
        toastIfSilent(err, "Couldn't upload the cover");
      }
    } finally {
      setUploading(false);
      onBusyChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !uploading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{replacing ? 'Replace cover' : 'Upload cover'}</DialogTitle>
          <DialogDescription>{COVER_HELP}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <div className="flex min-w-0 items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
              >
                Choose file
              </Button>
              <span className="min-w-0 truncate text-sm text-muted-foreground">
                {checking ? 'Checking...' : (file?.name ?? 'No file chosen')}
              </span>
              <input
                ref={fileInput}
                type="file"
                accept="image/webp,.webp"
                className="hidden"
                onChange={handleFile}
                disabled={uploading}
              />
            </div>
            {error && (
              <p role="alert" className="text-xs text-destructive">
                {error}
              </p>
            )}
          </div>
          {previewUrl && (
            <div className={`${BOX} aspect-[4/3]`}>
              <img src={previewUrl} alt="" draggable={false} className={IMAGE} />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button type="button" onClick={handleUpload} disabled={uploading || checking || !file}>
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface TemplateCoverCardProps {
  publicId: string;
  /** null when the template has no cover. */
  coverUrl: string | null;
  /** Why the buttons wait (unsaved catalogue edits: a cover write remounts the tab); null when they don't. */
  blockedReason: string | null;
  /** A write on the tab is running, this card's own included. */
  busy: boolean;
  onBusyChange: (busy: boolean) => void;
  /** Refetches the detail. */
  onChanged: () => Promise<unknown>;
}

export function TemplateCoverCard({
  publicId,
  coverUrl,
  blockedReason,
  busy,
  onBusyChange,
  onChanged,
}: TemplateCoverCardProps) {
  // The URL that failed to load: a new cover gets its own chance.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const missing = !!coverUrl && failedUrl === coverUrl;

  const handleRemove = async () => {
    if (removing) return;
    setRemoving(true);
    onBusyChange(true);
    try {
      await formTemplatesService.removeTemplateCover(publicId);
      toastService.success('Cover removed');
      setRemoveOpen(false);
      await onChanged();
    } catch (err) {
      if (isUnansweredCoverWrite(err)) {
        // The cover may have been removed: show what the server has.
        toastService.error(COVER_UNANSWERED_TEXT);
        setRemoveOpen(false);
        await onChanged();
      } else {
        // A server message was already toasted by the api client.
        toastIfSilent(err, "Couldn't remove the cover");
      }
    } finally {
      setRemoving(false);
      onBusyChange(false);
    }
  };

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="px-5 py-4 gap-0 border-b">
        <CardTitle className="text-base font-semibold">Cover</CardTitle>
        <CardDescription>The picture on the template's card</CardDescription>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="max-w-sm space-y-2">
          <CoverPreview coverUrl={coverUrl} missing={missing} onError={() => setFailedUrl(coverUrl)} />
          <p className="text-xs text-muted-foreground">{COVER_HELP}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GatedButton variant="outline" onClick={() => setUploadOpen(true)} disabled={busy} blockedReason={blockedReason}>
            <Upload className="mr-2 h-4 w-4" />
            {coverUrl ? 'Replace cover' : 'Upload cover'}
          </GatedButton>
          {coverUrl && (
            <GatedButton
              variant="outline"
              onClick={() => setRemoveOpen(true)}
              disabled={busy}
              blockedReason={blockedReason}
            >
              Remove
            </GatedButton>
          )}
        </div>
      </CardContent>

      <CoverUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        publicId={publicId}
        replacing={!!coverUrl}
        onBusyChange={onBusyChange}
        onChanged={onChanged}
      />

      <AlertDialog open={removeOpen} onOpenChange={(open) => !open && !removing && setRemoveOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove cover?</AlertDialogTitle>
            <AlertDialogDescription>Cards show a drawing of the first question instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                // Not while the dialog fades out: a click then must not remove again.
                if (removeOpen) void handleRemove();
              }}
              disabled={removing}
            >
              {removing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
